import { Brain, Users, ChevronDown, ChevronUp, Briefcase, Building2, Info } from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import { useState } from 'react';
import ProfessionalSkillTracker from './ProfessionalSkillTracker';

const SkillTracker = () => {
  const {
    careerStage,
    selectedPersonalityTraits,
    personalityTraits,
    selectedCareerDomains,
    careerDomains,
    industriesList,
    updatePersonalityTraits,
    updateCareerDomains,
    updateCareerStage,
  } = usePortfolio();

  const isFresher = careerStage === 'fresher';
  const isProfessional = careerStage === 'professional';

  // If professional, show the professional skill tracker
  if (isProfessional) {
    return <ProfessionalSkillTracker />;
  }

  const [expandedDomain, setExpandedDomain] = useState(null);

  const handleTraitToggle = (traitId) => {
    if (selectedPersonalityTraits.includes(traitId)) {
      updatePersonalityTraits(selectedPersonalityTraits.filter(id => id !== traitId));
    } else if (selectedPersonalityTraits.length < 20) {
      updatePersonalityTraits([...selectedPersonalityTraits, traitId]);
    }
  };

  const handleDomainToggle = (domainId) => {
    if (selectedCareerDomains.includes(domainId)) {
      updateCareerDomains(selectedCareerDomains.filter(id => id !== domainId));
    } else {
      updateCareerDomains([...selectedCareerDomains, domainId]);
    }
  };

  const toggleDomainExpansion = (domainId) => {
    setExpandedDomain(expandedDomain === domainId ? null : domainId);
  };

  const columns = 4;
  const traitsPerColumn = Math.ceil(personalityTraits.length / columns);
  const traitColumns = [];
  for (let i = 0; i < columns; i++) {
    traitColumns.push(personalityTraits.slice(i * traitsPerColumn, (i + 1) * traitsPerColumn));
  }

  return (
    <div className="space-y-3 sm:space-y-4 max-w-full overflow-hidden">
      {/* Personality Traits Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-hidden">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-[#fee2e2] rounded-lg flex-shrink-0">
            <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-[#c33c3d]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight truncate">
              Learner Traits
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              Select traits that best describe you
            </p>
          </div>
        </div>
      </div>

      {/* Personality Traits Content */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-hidden">
        <div className="flex flex-wrap gap-2">
          {personalityTraits.map((trait) => {
            const isSelected = selectedPersonalityTraits.includes(trait.id);
            return (
              <button
                key={trait.id}
                type="button"
                onClick={() => handleTraitToggle(trait.id)}
                disabled={!isSelected && selectedPersonalityTraits.length >= 20}
                className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  isSelected
                    ? 'bg-[#ef4444] text-white shadow-sm'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-[#ef4444] hover:text-[#ef4444] disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {trait.name}
              </button>
            );
          })}
        </div>

        {selectedPersonalityTraits.length > 0 && (
          <div className="border-t border-gray-100 pt-3 mt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm font-semibold text-gray-800">
                Selected ({selectedPersonalityTraits.length}/20)
              </span>
              <button
                type="button"
                onClick={() => updatePersonalityTraits([])}
                className="text-xs font-semibold text-[#ef4444] hover:text-[#dc2626]"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedPersonalityTraits.map((traitId) => {
                const trait = personalityTraits.find(t => t.id === traitId);
                return (
                  <span
                    key={traitId}
                    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-red-50 border border-red-200 rounded-full text-xs text-red-700 font-medium"
                  >
                    {trait?.name}
                    <button
                      type="button"
                      onClick={() => handleTraitToggle(traitId)}
                      className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Career Domains Header - hidden for Fresher */}
      {!isFresher && (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-visible">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-[#fee2e2] rounded-lg flex-shrink-0">
            <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-[#c33c3d]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight truncate">
                {careerStage === 'student' ? 'Academic Disciplines' : 'Career Domains'}
              </h2>
              {careerStage === 'student' && (
                <div className="relative group flex-shrink-0">
                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block w-72 sm:w-96 z-50">
                    <div className="bg-[#fee2e2] text-gray-800 text-xs rounded-lg p-3 shadow-lg border border-[#fecaca]">
                      <p className="leading-relaxed">
                        Choose the academic discipline you wish to study and explore through a suitable degree program. Carefully select the field that aligns with your interests and goals, as it will shape your learning journey and future opportunities.
                      </p>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-1">
                        <div className="border-4 border-transparent border-b-[#fee2e2]"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              {careerStage === 'student'
                ? 'Explore different academic disciplines'
                : 'Explore different career domains'}
            </p>
          </div>
        </div>
      </div>
      )}

      {/* Career Domains Content - hidden for Fresher */}
      {!isFresher && (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-hidden">
        {careerDomains.length === 0 ? (
          <div className="text-xs sm:text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg px-3 py-6 text-center">
            No domains available.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {careerDomains.map((domain) => (
              <div
                key={domain.id}
                className={`border rounded-lg transition-all ${
                  selectedCareerDomains.includes(domain.id)
                    ? 'border-secondary-500 bg-secondary-50'
                    : 'border-gray-300 bg-white'
                }`}
              >
                <div className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-2 sm:gap-3">
                    <label className="flex items-start gap-2 sm:gap-3 cursor-pointer flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedCareerDomains.includes(domain.id)}
                        onChange={() => handleDomainToggle(domain.id)}
                        className="mt-0.5 sm:mt-1 w-4 h-4 sm:w-5 sm:h-5 text-secondary-500 border-gray-300 rounded focus:ring-secondary-500 cursor-pointer flex-shrink-0"
                        style={{ accentColor: '#e7474b' }}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold transition-colors break-words ${
                          selectedCareerDomains.includes(domain.id)
                            ? 'text-secondary-600'
                            : 'text-gray-900'
                        }`}>
                          {domain.name}
                        </h3>
                      </div>
                    </label>
                    <button
                      onClick={() => toggleDomainExpansion(domain.id)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                    >
                      {expandedDomain === domain.id ? (
                        <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                      )}
                    </button>
                  </div>

                  {expandedDomain === domain.id && (
                    <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
                      <p className="text-xs sm:text-sm text-gray-600">
                        {domain.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default SkillTracker;
